import { TagProps } from '@indigo-ag/component-module';
import {
  BackgroundCheck,
  BackgroundCheckDisplayStatus,
  BackgroundCheckStepEventType,
  BackgroundCheckStepStatus,
  BackgroundCheckStepType,
  OpportunityContactRole
} from '@indigo-ag/schema';
import { getTranslatedRole } from 'features/brazil-grower/pages/ContactDetailsPage/utils';
import { STEP_DECISION_REASON_CODE } from 'features/brazil-grower/shared-br-grower';
import i18next from 'i18next';

export const getStepStatusTranslation = (status: BackgroundCheckStepStatus): string => {
  switch (status) {
    case BackgroundCheckStepStatus.NOT_STARTED:
      return i18next.t('backgroundCheckStepStatus_NotStarted');
    case BackgroundCheckStepStatus.PROCESSING:
      return i18next.t('backgroundCheckStepStatus_Processing');
    case BackgroundCheckStepStatus.SYSTEM_ERROR:
      return i18next.t('backgroundCheckStepStatus_SystemError');
    case BackgroundCheckStepStatus.PASSED:
      return i18next.t('backgroundCheckStepStatus_Passed');
    case BackgroundCheckStepStatus.PASSED_TENTATIVE:
      return i18next.t('backgroundCheckStepStatus_PassedTentative');
    case BackgroundCheckStepStatus.FAILED:
      return i18next.t('backgroundCheckStepStatus_Failed');
    case BackgroundCheckStepStatus.FAILED_TENTATIVE:
      return i18next.t('backgroundCheckStepStatus_FailedTentative');
    default:
      return status;
  }
};

export const getStepTypeTranslation = (stepType: BackgroundCheckStepType): string => {
  switch (stepType) {
    case BackgroundCheckStepType.EMBARGO:
      return i18next.t('embargoInfraction');
    case BackgroundCheckStepType.NEGATIVE_DEBT:
      return i18next.t('negativeDebt');
    case BackgroundCheckStepType.FORCED_LABOR:
      return i18next.t('laborSanctions');
    default:
      return stepType;
  }
};

export const getCheckStatusTranslation = (status: BackgroundCheckDisplayStatus): string => {
  switch (status) {
    case BackgroundCheckDisplayStatus.IN_PROGRESS:
      return i18next.t('backgroundCheckDisplayStatus_InProgress');
    case BackgroundCheckDisplayStatus.FAILED:
      return i18next.t('backgroundCheckDisplayStatus_Failed');
    case BackgroundCheckDisplayStatus.EXPIRED:
      return i18next.t('backgroundCheckDisplayStatus_Expired');
    case BackgroundCheckDisplayStatus.CLEARED:
      return i18next.t('backgroundCheckDisplayStatus_Cleared');
    case BackgroundCheckDisplayStatus.UNRESOLVED:
      return i18next.t('backgroundCheckDisplayStatus_Unresolved');
    default:
      return status;
  }
};

export const getCheckStatusVariant = (status: string | undefined): string | undefined => {
  switch (status) {
    case BackgroundCheckDisplayStatus.IN_PROGRESS:
      return 'warning';
    case BackgroundCheckDisplayStatus.FAILED:
      return 'error';
    case BackgroundCheckDisplayStatus.EXPIRED:
      return 'error';
    case BackgroundCheckDisplayStatus.CLEARED:
      return 'success';
    case BackgroundCheckDisplayStatus.UNRESOLVED:
      return 'error';
    default:
      return undefined;
  }
};

export const getCheckStepStatusVariant = (status: BackgroundCheckStepStatus): Exclude<TagProps['variant'], 'info'> => {
  switch (status) {
    case BackgroundCheckStepStatus.PASSED:
      return 'success';
    case BackgroundCheckStepStatus.FAILED:
    case BackgroundCheckStepStatus.FAILED_TENTATIVE:
    case BackgroundCheckStepStatus.SYSTEM_ERROR:
      return 'error';
    case BackgroundCheckStepStatus.PASSED_TENTATIVE:
      return 'warning';
    default:
      return 'neutral';
  }
};

export const getDetailsModalTitle = (stepType: BackgroundCheckStepType): string => {
  switch (stepType) {
    case BackgroundCheckStepType.NEGATIVE_DEBT:
      return i18next.t('backgroundCheckDetailsModalTitle_DebtDetails');
    case BackgroundCheckStepType.EMBARGO:
      return i18next.t('backgroundCheckDetailsModalTitle_EmbargoDetails');
    case BackgroundCheckStepType.FORCED_LABOR:
      return i18next.t('backgroundCheckDetailsModalTitle_LaborSanctionDetails');
    default:
      return stepType;
  }
};

export const getEventTypeTranslation = (eventType: BackgroundCheckStepEventType): string => {
  switch (eventType) {
    case BackgroundCheckStepEventType.ACCEPTED_DECISION:
      return i18next.t('backgroundCheckStepEvent_AcceptedDecision');
    case BackgroundCheckStepEventType.OVERRODE_DECISION_FAIL:
      return i18next.t('backgroundCheckStepEvent_OverrodeDecisionFail');
    case BackgroundCheckStepEventType.OVERRODE_DECISION_PASS:
      return i18next.t('backgroundCheckStepEvent_OverrodeDecisionPass');
    case BackgroundCheckStepEventType.RETRIED:
      return i18next.t('backgroundCheckStepEvent_Retried');
    case BackgroundCheckStepEventType.STARTED_PROCESSING:
      return i18next.t('backgroundCheckStepEvent_StartedProcessing');
    case BackgroundCheckStepEventType.SYSTEM_ERRORED:
      return i18next.t('backgroundCheckStepEvent_SystemErrored');
    case BackgroundCheckStepEventType.SYSTEM_ACCEPTED_DECISION:
      return i18next.t('backgroundCheckStepEvent_SystemAcceptedDecision');
    case BackgroundCheckStepEventType.SYSTEM_PASSED:
      return i18next.t('backgroundCheckStepEvent_SystemPassed');
    case BackgroundCheckStepEventType.SYSTEM_TENTATIVELY_FAILED:
      return i18next.t('backgroundCheckStepEvent_SystemTentativelyFailed');
    case BackgroundCheckStepEventType.SYSTEM_TENTATIVELY_PASSED:
      return i18next.t('backgroundCheckStepEvent_SystemTentativelyPassed');
    default:
      return eventType;
  }
};

export const getCommentTranslation = (comment: string) => {
  switch (comment) {
    case STEP_DECISION_REASON_CODE.FAILED_ENVIRONMENTAL_EMBARGO.valueOf():
      return i18next.t('backgroundCheckStepEventComment_FailedEmbargo');
    case STEP_DECISION_REASON_CODE.FAILED_FORCED_LABOR_VIOLATION.valueOf():
      return i18next.t('backgroundCheckStepEventComment_FailedLabor');
    case STEP_DECISION_REASON_CODE.FAILED_OUTSTANDING_DEBTS.valueOf():
      return i18next.t('backgroundCheckStepEventComment_FailedDebt');
    case STEP_DECISION_REASON_CODE.PASSED_NOTHING_PROBLEMATIC_FOUND.valueOf():
      return undefined;
    default:
      return comment;
  }
};

export const getBackgroundCheckMap = (backgroundChecks: BackgroundCheck[]) => {
  return backgroundChecks.reduce((result: Record<string, BackgroundCheck>, backgroundCheck: BackgroundCheck) => {
    result[backgroundCheck.taxIdentifier] = backgroundCheck;
    return result;
  }, {});
};

export const getUniqueContactRoles = (contactRoles: OpportunityContactRole[]): OpportunityContactRole[] => {
  const uniqueContactIds: Record<string, number> = {};
  const uniqueContactRoles = [];

  for (const role of contactRoles) {
    if (!role.isPrimary && uniqueContactIds[role.contactId]) {
      // Continue on if the contact has already been added to the list
      continue;
    }

    uniqueContactRoles.push(role);
    uniqueContactIds[role.contactId] = 1;
  }

  return uniqueContactRoles;
};

export const getContactNameRole = ({ role, contact }: OpportunityContactRole): string => {
  return `${contact.name} - ${getTranslatedRole(role)}`;
};
